import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, map, Observable, Subscription, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HeadersService } from '../../core/services/headers.service';
import {
    AI_CHATBOT_ERROR_KEYS,
    AiChatbotChatRequest,
    AiChatbotChatResponse,
    AiChatbotStreamError,
    AiChatbotStreamEvent,
    RawAiChatbotChatResponse,
} from '../models/ai-chatbot.model';

@Injectable({
    providedIn: 'root',
})
export class AiChatbotService {
    private readonly endpoint = 'api/ai/chatbot/chat';
    private readonly streamEndpoint = 'api/ai/chatbot/chat/stream';
    private readonly useAzureMockHeader = 'use_azure_mock';

    constructor(
        private http: HttpClient,
        private headersService: HeadersService,
    ) {}

    public chat(request: AiChatbotChatRequest, useAzureMock: boolean): Observable<AiChatbotChatResponse> {
        const response$ = this.http
            .post<RawAiChatbotChatResponse>(this.endpoint, request, {
                headers: this.createHeaders(useAzureMock),
            })
            .pipe(
                map(response => {
                    return {
                        threadId: response.threadId ?? response.ThreadId ?? '',
                        responseText: this.removeInternalFoundryMarkers(response.responseText ?? response.ResponseText ?? ''),
                    };
                }),
            );

        if (useAzureMock && environment.aiChatbotMockDelayMs > 0) {
            return response$.pipe(delay(environment.aiChatbotMockDelayMs));
        }

        return response$;
    }

    public chatStreamWithFallback(request: AiChatbotChatRequest, useAzureMock: boolean): Observable<AiChatbotStreamEvent> {
        return new Observable<AiChatbotStreamEvent>(observer => {
            let fallbackSubscription: Subscription | null = null;
            const abortController = new AbortController();
            const timeoutMs = environment.aiChatbotStreamTimeoutMs;
            let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
            let timedOut = false;
            let streamSignalReceived = false;
            let partialResponseText = '';
            let streamCompleted = false;

            const cleanup = () => {
                if (timeoutHandle) {
                    clearTimeout(timeoutHandle);
                    timeoutHandle = null;
                }
            };

            const emitCompleted = (responseText: string, threadId?: string) => {
                if (streamCompleted) {
                    return;
                }

                streamCompleted = true;
                observer.next({ type: 'completed', responseText, threadId });
                observer.complete();
            };

            const fallbackToClassicEndpoint = () => {
                fallbackSubscription = this.chat(request, useAzureMock)
                    .pipe(take(1))
                    .subscribe({
                        next: response => {
                            if (response.threadId) {
                                observer.next({ type: 'thread_started', threadId: response.threadId });
                            }

                            if (response.responseText) {
                                const responseText = this.removeInternalFoundryMarkers(response.responseText);
                                observer.next({ type: 'delta', deltaText: responseText, responseText });
                            }

                            emitCompleted(this.removeInternalFoundryMarkers(response.responseText ?? ''), response.threadId);
                        },
                        error: () => {
                            const fallbackError: AiChatbotStreamError = {
                                message: AI_CHATBOT_ERROR_KEYS.requestFailed,
                            };
                            observer.error(fallbackError);
                        },
                    });
            };

            if (!this.shouldUseStreaming()) {
                fallbackToClassicEndpoint();
                return () => fallbackSubscription?.unsubscribe();
            }

            timeoutHandle = setTimeout(() => {
                timedOut = true;
                abortController.abort();
            }, timeoutMs);

            void this.readSseStream(request, useAzureMock, abortController.signal, (eventType, rawData) => {
                const payload = this.tryParseJson(rawData);
                const normalizedEventType = this.normalizeEventType(eventType, payload);

                switch (normalizedEventType) {
                    case 'thread_started': {
                        streamSignalReceived = true;
                        const threadId = this.extractThreadId(payload);
                        if (threadId) {
                            observer.next({ type: 'thread_started', threadId });
                        }
                        break;
                    }
                    case 'delta': {
                        streamSignalReceived = true;
                        const deltaText = this.extractDeltaText(payload, rawData);
                        if (!deltaText) {
                            break;
                        }

                        partialResponseText += deltaText;
                        observer.next({
                            type: 'delta',
                            deltaText,
                            responseText: this.removeInternalFoundryMarkers(partialResponseText),
                        });
                        break;
                    }
                    case 'completed': {
                        streamSignalReceived = true;
                        const completedText = this.removeInternalFoundryMarkers(this.extractCompletedText(payload, rawData, partialResponseText));
                        const threadId = this.extractThreadId(payload);
                        emitCompleted(completedText, threadId);
                        break;
                    }
                    case 'error': {
                        streamSignalReceived = true;
                        const message = this.extractErrorMessage(payload, rawData);
                        throw new Error(message);
                    }
                    default:
                        break;
                }
            })
                .then(() => {
                    cleanup();

                    if (streamCompleted) {
                        return;
                    }

                    emitCompleted(this.removeInternalFoundryMarkers(partialResponseText));
                })
                .catch(error => {
                    cleanup();

                    const canFallback = !streamSignalReceived && !timedOut;
                    if (canFallback) {
                        fallbackToClassicEndpoint();
                        return;
                    }

                    const streamError: AiChatbotStreamError = {
                        message: timedOut ? AI_CHATBOT_ERROR_KEYS.timeout : this.extractRuntimeErrorMessage(error),
                        partialResponseText: this.removeInternalFoundryMarkers(partialResponseText) || undefined,
                    };
                    observer.error(streamError);
                });

            return () => {
                abortController.abort();
                cleanup();
                fallbackSubscription?.unsubscribe();
            };
        });
    }

    private shouldUseStreaming(): boolean {
        if (!environment.aiChatbotUseStreaming) {
            return false;
        }

        return typeof window !== 'undefined' && typeof window.fetch === 'function' && typeof TextDecoder !== 'undefined';
    }

    private async readSseStream(request: AiChatbotChatRequest, useAzureMock: boolean, signal: AbortSignal, onEvent: (eventType: string, data: string) => void): Promise<void> {
        const response = await fetch(this.streamEndpoint, {
            method: 'POST',
            headers: this.createStreamHeaders(useAzureMock),
            body: JSON.stringify(request),
            signal,
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(AI_CHATBOT_ERROR_KEYS.requestFailed);
        }

        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('text/event-stream')) {
            throw new Error(AI_CHATBOT_ERROR_KEYS.requestFailed);
        }

        if (!response.body) {
            throw new Error(AI_CHATBOT_ERROR_KEYS.requestFailed);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }

            buffer += decoder.decode(value, { stream: true });
            buffer = this.consumeSseBuffer(buffer, onEvent);
        }

        buffer += decoder.decode();
        this.consumeSseBuffer(buffer, onEvent);
    }

    private consumeSseBuffer(buffer: string, onEvent: (eventType: string, data: string) => void): string {
        let separator = this.findSseSeparator(buffer);
        while (separator) {
            const block = buffer.slice(0, separator.index);
            buffer = buffer.slice(separator.index + separator.length);
            if (block.trim()) {
                const { eventType, data } = this.parseSseBlock(block);
                if (eventType && data !== undefined) {
                    onEvent(eventType, data);
                }
            }
            separator = this.findSseSeparator(buffer);
        }

        return buffer;
    }

    private findSseSeparator(buffer: string): { index: number; length: number } | null {
        const unixSeparator = buffer.indexOf('\n\n');
        const windowsSeparator = buffer.indexOf('\r\n\r\n');

        if (unixSeparator < 0) {
            return windowsSeparator < 0 ? null : { index: windowsSeparator, length: 4 };
        }
        if (windowsSeparator < 0) {
            return { index: unixSeparator, length: 2 };
        }
        return unixSeparator < windowsSeparator ? { index: unixSeparator, length: 2 } : { index: windowsSeparator, length: 4 };
    }

    private parseSseBlock(block: string): { eventType: string; data: string } {
        const lines = block.split(/\r?\n/);
        let eventType = 'message';
        const dataLines: string[] = [];

        for (const line of lines) {
            if (!line || line.startsWith(':')) {
                continue;
            }

            if (line.startsWith('event:')) {
                eventType = line.slice(6).trim();
                continue;
            }

            if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trimStart());
            }
        }

        return { eventType, data: dataLines.join('\n') };
    }

    private tryParseJson(rawData: string): Record<string, unknown> | null {
        if (!rawData) {
            return null;
        }

        try {
            const payload = JSON.parse(rawData);
            if (payload && typeof payload === 'object') {
                return payload as Record<string, unknown>;
            }
        } catch {
            return null;
        }

        return null;
    }

    private normalizeEventType(eventType: string, payload: Record<string, unknown> | null): string {
        if (eventType !== 'message') {
            return eventType;
        }

        if (!payload) {
            return eventType;
        }

        const payloadType = payload['event'] ?? payload['type'] ?? payload['Event'] ?? payload['Type'];
        if (typeof payloadType === 'string' && payloadType.trim()) {
            return payloadType.trim();
        }

        return eventType;
    }

    private extractThreadId(payload: Record<string, unknown> | null): string {
        if (!payload) {
            return '';
        }

        const threadId = payload['threadId'] ?? payload['ThreadId'];
        return typeof threadId === 'string' ? threadId : '';
    }

    private extractDeltaText(payload: Record<string, unknown> | null, rawData: string): string {
        if (payload) {
            const delta = payload['delta'] ?? payload['Delta'] ?? payload['responseText'] ?? payload['ResponseText'] ?? payload['text'] ?? payload['Text'];
            if (typeof delta === 'string') {
                return delta;
            }
            return '';
        }

        return rawData ?? '';
    }

    private extractCompletedText(payload: Record<string, unknown> | null, rawData: string, partialResponseText: string): string {
        if (payload) {
            const completedText = payload['responseText'] ?? payload['ResponseText'] ?? payload['text'] ?? payload['Text'];
            if (typeof completedText === 'string') {
                return completedText;
            }
        }

        if (rawData) {
            return rawData;
        }

        return partialResponseText;
    }

    private removeInternalFoundryMarkers(responseText: string): string {
        return responseText.replace(/\uE200[^\uE201]*\uE201/g, '').replace(/\uE200[^\uE201]*$/g, '');
    }

    private extractErrorMessage(payload: Record<string, unknown> | null, rawData: string): string {
        if (payload) {
            const message = payload['message'] ?? payload['Message'] ?? payload['error'] ?? payload['Error'] ?? payload['detail'] ?? payload['Detail'];
            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }

        if (rawData?.trim()) {
            return rawData;
        }

        return AI_CHATBOT_ERROR_KEYS.requestFailed;
    }

    private extractRuntimeErrorMessage(error: unknown): string {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        return AI_CHATBOT_ERROR_KEYS.requestFailed;
    }

    private createHeaders(useAzureMock: boolean): HttpHeaders {
        let headers = this.headersService.getJsonHeader();
        if (useAzureMock) {
            headers = headers.append(this.useAzureMockHeader, '1');
        }

        return headers;
    }

    private createStreamHeaders(useAzureMock: boolean): HeadersInit {
        const headers = this.createHeaders(useAzureMock).set('Accept', 'text/event-stream');
        const entries: Record<string, string> = {};
        for (const key of headers.keys()) {
            const value = headers.get(key);
            if (value) {
                entries[key] = value;
            }
        }
        return entries;
    }
}
